/**
 * TasksView Component
 *
 * Main view for Task Management within a project.
 * Integrates TaskBoard with React Query data fetching and WebSocket subscriptions.
 *
 * @module components/tasks/TasksView
 */

import { memo, useState, useCallback, useMemo, useRef } from 'react';
import { Loader2, AlertCircle, RefreshCw, AlertTriangle } from 'lucide-react';
import TaskBoard from './TaskBoard.jsx';
import TaskDetail from './TaskDetail.jsx';
import TaskForm from './TaskForm.jsx';
import TaskFilters from './TaskFilters.jsx';
import ConfirmDialog from '../common/ConfirmDialog.jsx';
import {
  useTasks,
  useTask,
  useTaskComments,
  useTaskHistory,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  useUpdateTaskStatus,
  useLabels,
  useProjectAgents
} from '../../hooks/useTasks.js';
import { useTaskSubscription } from '../../hooks/useTaskSubscription.js';
import { useWebSocketState, CONNECTION_STATE } from '../../hooks/useWebSocket.js';

// ============================================
// Constants
// ============================================

const TASK_STATUS_ORDER = ['backlog', 'todo', 'doing', 'agent_test', 'user_test', 'review', 'testing', 'done', 'blocked'];

// ============================================
// Main Component
// ============================================

/**
 * TasksView Component
 *
 * @param {object} props - Component props
 * @param {object} props.project - Current project object
 * @param {array} [props.projects=[]] - All available projects for selector
 * @param {function} [props.onProjectChange] - Callback when project is changed
 * @param {function} [props.onBack] - Callback when back button is clicked
 * @param {boolean} [props.isPopout=false] - Whether this is rendered in a popout window
 * @returns {React.ReactElement} Tasks view element
 */
const TasksView = memo(function TasksView({
  project,
  projects = [],
  onProjectChange,
  onBack,
  isPopout = false
}) {
  // ========================================
  // State
  // ========================================

  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [highlightedTaskIds, setHighlightedTaskIds] = useState(new Set());
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, task: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [filters, setFilters] = useState({
    searchQuery: '',
    status: [],
    priority: [],
    labels: [],
    assigneeId: null
  });
  const highlightTimeoutsRef = useRef(new Map());

  // ========================================
  // Highlight Effect for Live Updates
  // ========================================

  const highlightTask = useCallback((taskId, duration = 3000) => {
    // Add to highlighted set
    setHighlightedTaskIds(prev => new Set([...prev, taskId]));

    // Clear any existing timeout for this task
    const existingTimeout = highlightTimeoutsRef.current.get(taskId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Remove highlight after duration
    const timeoutId = setTimeout(() => {
      setHighlightedTaskIds(prev => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
      highlightTimeoutsRef.current.delete(taskId);
    }, duration);

    highlightTimeoutsRef.current.set(taskId, timeoutId);
  }, []);

  // ========================================
  // Data Fetching
  // ========================================

  // Debug: Log project info
  console.log('[TasksView] project:', project?.id, project?.name, 'enabled:', !!project?.id);

  const {
    data: tasksData,
    isLoading: isLoadingTasks,
    error: tasksError,
    refetch: refetchTasks
  } = useTasks({ projectId: project?.id, limit: 100 }, { enabled: !!project?.id });

  // Debug: Log tasks data
  console.log('[TasksView] tasksData:', tasksData, 'isLoading:', isLoadingTasks, 'error:', tasksError);

  const {
    data: selectedTask,
    isLoading: isLoadingTask
  } = useTask(selectedTaskId, { enabled: !!selectedTaskId });

  const {
    data: commentsData,
    isLoading: isLoadingComments
  } = useTaskComments(selectedTaskId, {}, { enabled: !!selectedTaskId });

  const {
    data: historyData,
    isLoading: isLoadingHistory
  } = useTaskHistory(selectedTaskId, {}, { enabled: !!selectedTaskId });

  // Fetch labels and assignees for the project
  const { data: labelsData = [] } = useLabels(project?.id, { enabled: !!project?.id });
  const { data: assigneesData = [] } = useProjectAgents(project?.id, { enabled: !!project?.id });

  // ========================================
  // Mutations
  // ========================================

  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const updateTaskStatus = useUpdateTaskStatus();

  // ========================================
  // WebSocket State & Subscription
  // ========================================

  const { state: wsState, isConnected: wsConnected } = useWebSocketState();

  const { isSubscribed } = useTaskSubscription(project?.id, {
    enabled: !!project?.id,
    onTaskCreated: (task) => {
      console.log('[WS] Task created:', task.title);
      // Highlight newly created task
      highlightTask(task.id);
    },
    onTaskUpdated: (taskId, changes) => {
      console.log('[WS] Task updated:', taskId, changes);
      // Highlight updated task
      highlightTask(taskId);
    },
    onTaskDeleted: (taskId) => {
      console.log('[WS] Task deleted:', taskId);
    },
    onTaskStatusChanged: (taskId, prevStatus, newStatus, changedBy) => {
      console.log(`[WS] Task ${taskId} moved: ${prevStatus} → ${newStatus} by ${changedBy}`);
      // Highlight task that moved to a new column
      highlightTask(taskId);
    },
    onTaskAssigned: (taskId, assignee) => {
      console.log('[WS] Task assigned:', taskId, assignee?.name);
      highlightTask(taskId);
    },
    onTaskUnassigned: (taskId) => {
      console.log('[WS] Task unassigned:', taskId);
      highlightTask(taskId);
    },
    onTaskCommentAdded: (taskId, comment) => {
      console.log('[WS] Comment added to task:', taskId);
      highlightTask(taskId);
    }
  });

  // ========================================
  // Computed Values
  // ========================================

  // Group tasks by status for the Kanban board
  const tasksByStatus = useMemo(() => {
    // API returns data in tasksData.data or tasksData.items
    const tasks = tasksData?.data || tasksData?.items || [];
    if (tasks.length === 0) return {};

    let filteredTasks = tasks;

    // Apply filters
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filteredTasks = filteredTasks.filter(t =>
        t.title.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query)
      );
    }

    if (filters.status?.length > 0) {
      filteredTasks = filteredTasks.filter(t => filters.status.includes(t.status));
    }

    if (filters.priority?.length > 0) {
      filteredTasks = filteredTasks.filter(t => filters.priority.includes(t.priority));
    }

    if (filters.labels?.length > 0) {
      filteredTasks = filteredTasks.filter(t =>
        t.labels?.some(l => filters.labels.includes(l.id))
      );
    }

    if (filters.assigneeId) {
      if (filters.assigneeId === 'unassigned') {
        filteredTasks = filteredTasks.filter(t => !t.assigneeId);
      } else {
        filteredTasks = filteredTasks.filter(t => t.assigneeId === filters.assigneeId);
      }
    }

    // Group by status
    const grouped = {};
    TASK_STATUS_ORDER.forEach(status => {
      grouped[status] = filteredTasks.filter(t => t.status === status);
    });

    return grouped;
  }, [tasksData, filters]);

  // Check if tasks are truncated due to pagination limit
  const tasksTruncated = useMemo(() => {
    const pagination = tasksData?.pagination;
    if (!pagination) return null;
    const loadedCount = tasksData?.data?.length || tasksData?.items?.length || 0;
    if (pagination.total > loadedCount) {
      return {
        loaded: loadedCount,
        total: pagination.total,
        hidden: pagination.total - loadedCount
      };
    }
    return null;
  }, [tasksData]);

  // ========================================
  // Handlers
  // ========================================

  const handleTaskClick = useCallback((task) => {
    setSelectedTaskId(task.id);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedTaskId(null);
  }, []);

  const handleCreateTask = useCallback(() => {
    setEditingTask(null);
    setIsFormOpen(true);
  }, []);

  const handleEditTask = useCallback((task) => {
    setEditingTask(task);
    setIsFormOpen(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingTask(null);
  }, []);

  const handleSubmitForm = useCallback(async (data) => {
    try {
      if (editingTask) {
        await updateTask.mutateAsync({ id: editingTask.id, data });
      } else {
        await createTask.mutateAsync({ ...data, projectId: project.id });
      }
      handleCloseForm();
      // Explicitly refetch tasks to ensure UI updates
      refetchTasks();
    } catch (error) {
      console.error('Failed to save task:', error);
    }
  }, [editingTask, updateTask, createTask, project?.id, handleCloseForm, refetchTasks]);

  const handleDeleteTask = useCallback((task) => {
    setDeleteDialog({ isOpen: true, task });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteDialog.task) return;
    setIsDeleting(true);
    try {
      await deleteTask.mutateAsync(deleteDialog.task.id);
      if (selectedTaskId === deleteDialog.task.id) {
        setSelectedTaskId(null);
      }
      setDeleteDialog({ isOpen: false, task: null });
    } catch (error) {
      console.error('Failed to delete task:', error);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteDialog.task, deleteTask, selectedTaskId]);

  const handleCancelDelete = useCallback(() => {
    setDeleteDialog({ isOpen: false, task: null });
  }, []);

  const handleStatusChange = useCallback(async (taskId, newStatus) => {
    try {
      // Optimistic update happens in useUpdateTaskStatus hook
      // No need to refetch - the mutation handles cache updates
      await updateTaskStatus.mutateAsync({ id: taskId, status: newStatus });
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  }, [updateTaskStatus]);

  const handleSaveTask = useCallback(async (taskId, data) => {
    try {
      await updateTask.mutateAsync({ id: taskId, data });
      refetchTasks();
      return true;
    } catch (error) {
      console.error('Failed to save task:', error);
      return false;
    }
  }, [updateTask, refetchTasks]);

  const toggleFilters = useCallback(() => {
    setIsFiltersOpen(prev => !prev);
  }, []);

  // WebSocket status object for TaskBoard (must be before early returns!)
  const wsStatusObj = useMemo(() => ({
    isConnected: wsConnected,
    isSubscribed,
    state: wsState === CONNECTION_STATE.RECONNECTING ? 'reconnecting' : wsConnected ? 'connected' : 'disconnected'
  }), [wsConnected, isSubscribed, wsState]);

  // ========================================
  // Render
  // ========================================

  if (!project) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <AlertCircle className="w-6 h-6 mr-2" />
        No project selected
      </div>
    );
  }

  if (isLoadingTasks) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading tasks...
      </div>
    );
  }

  if (tasksError) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-red-400">
        <AlertCircle className="w-8 h-8 mb-2" />
        <p className="mb-4">Failed to load tasks</p>
        <button
          onClick={() => refetchTasks()}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* Truncation Warning Banner */}
      {tasksTruncated && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 border-b border-amber-500/30 text-amber-300 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>
            Showing {tasksTruncated.loaded} of {tasksTruncated.total} tasks.
            {tasksTruncated.hidden} task{tasksTruncated.hidden !== 1 ? 's' : ''} not displayed due to limit.
          </span>
        </div>
      )}
      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Main Board Area */}
        <div className="flex-1 overflow-hidden">
          <TaskBoard
            tasksByStatus={tasksByStatus}
            onTaskClick={handleTaskClick}
            onStatusChange={handleStatusChange}
            onCreateTask={handleCreateTask}
            highlightedTaskIds={highlightedTaskIds}
            isPopout={isPopout}
            showPopoutButton={!isPopout}
            wsStatus={wsStatusObj}
            projects={projects}
            selectedProject={project}
            onProjectChange={onProjectChange}
          />
        </div>

        {/* Filters Panel */}
        {isFiltersOpen && (
          <div className="w-80 border-l border-gray-800 overflow-y-auto">
            <TaskFilters
              filters={filters}
              onFiltersChange={setFilters}
              availableLabels={labelsData}
              availableAssignees={assigneesData}
              isOpen={true}
              onClose={() => setIsFiltersOpen(false)}
            />
          </div>
        )}

        {/* Detail Panel */}
        {selectedTaskId && (
          <div className="w-96 border-l border-gray-800 overflow-y-auto">
            <TaskDetail
              task={selectedTask?.data || selectedTask}
              comments={commentsData?.data || commentsData?.items || []}
              history={historyData?.data || historyData?.items || []}
              assignees={assigneesData}
              isLoadingComments={isLoadingComments}
              isLoadingHistory={isLoadingHistory}
              onClose={handleCloseDetail}
              onSave={handleSaveTask}
              onDelete={handleDeleteTask}
              isSaving={updateTask.isPending}
            />
          </div>
        )}
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg mx-4">
            <TaskForm
              task={editingTask}
              projects={[project]}
              labels={labelsData}
              assignees={assigneesData}
              onSubmit={handleSubmitForm}
              onCancel={handleCloseForm}
              isSubmitting={createTask.isPending || updateTask.isPending}
              submitError={createTask.error?.message || updateTask.error?.message}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title="Delete Task"
        itemName={deleteDialog.task?.title}
        warning="This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isProcessing={isDeleting}
      />
    </div>
  );
});

export default TasksView;
